import dayjs from 'dayjs';
import {
  goalHealthBarOrder,
  goalListStatusBarOrder,
} from '@ui/style/goal-status';
import {isActiveWorkspaceUser} from '@utils/active-workspace-user';
import {goalAttentionStatus, GoalAttentionStatus} from '@utils/goal-status';
import {goalProgress, sortMyGoals} from '../my-objective/my-goals-v2.utils';
import {MyGoalsSort} from '../my-objective/my-goals-v2.types';
import {
  TeamGoalMember,
  TeamGoalStatusSection,
  TeamGoalSummary,
} from './team-goals.types';

export const teamStatusOrder: GoalAttentionStatus[] = [
  ...goalListStatusBarOrder,
];

export const teamStatusLabels: Record<GoalAttentionStatus, string> = {
  overdue: 'Overdue',
  behind: 'Behind',
  at_risk: 'At risk',
  on_track: 'On track',
  upcoming: 'Upcoming',
  no_status: 'No status',
};

const userId = (user: any) => user?.id || user?._id || '';

const reviewerId = (user: any) =>
  typeof user?.reviewer === 'string' ? user.reviewer : user?.reviewer?.id;

export const teamMemberName = (user: any) => {
  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  return fullName || user?.name || user?.email || 'Team member';
};

export const buildTeamGoalMembers = ({
  reports,
  users,
  managerId,
  memberIds,
  searching = false,
}: {
  reports: TeamGoalMember[] | null | undefined;
  users: any[];
  managerId?: string;
  memberIds?: string[];
  searching?: boolean;
}): TeamGoalMember[] => {
  const reportList = Array.isArray(reports) ? reports : [];
  const activeUserIds = new Set(
    users.filter(isActiveWorkspaceUser).map(userId),
  );
  const eligibleReports = reportList.filter(
    (report) =>
      isActiveWorkspaceUser(report?.assignee) &&
      (!users.length || activeUserIds.has(userId(report.assignee))),
  );
  const reportByUser = new Map(
    eligibleReports.map((report) => [userId(report.assignee), report]),
  );
  const selectedMembers = new Set((memberIds || []).filter(Boolean));
  const roster = users.filter((user) => {
    if (!isActiveWorkspaceUser(user)) return false;
    if (selectedMembers.size) return selectedMembers.has(userId(user));
    return Boolean(managerId) && reviewerId(user) === managerId;
  });

  const candidates = [
    ...eligibleReports.map((report) => report.assignee),
    ...(searching ? [] : roster),
  ];
  const uniqueUsers = Array.from(
    new Map(
      candidates
        .filter((user) => userId(user))
        .map((user) => [userId(user), user]),
    ).values(),
  );

  return uniqueUsers.map((assignee) => ({
    assignee,
    goals: reportByUser.get(userId(assignee))?.goals || [],
  }));
};

export const summarizeTeamGoals = (goals: any[]): TeamGoalSummary => {
  const byPerformance = {
    overdue: 0,
    on_track: 0,
    at_risk: 0,
    behind: 0,
    upcoming: 0,
    no_status: 0,
  } as Record<GoalAttentionStatus, number>;

  goals.forEach((goal) => {
    byPerformance[goalAttentionStatus(goal)] += 1;
  });

  const total = goals.length;
  const averageProgress = total
    ? Math.round(
        goals.reduce((sum, goal) => sum + goalProgress(goal), 0) / total,
      )
    : 0;

  return {
    total,
    averageProgress,
    byPerformance,
  };
};

const memberSortValue = (member: TeamGoalMember, sort: MyGoalsSort) => {
  if (sort === 'highest_progress') {
    return summarizeTeamGoals(member.goals).averageProgress;
  }

  const field = sort === 'due_date' ? 'endDate' : 'createdAt';
  const dates = member.goals
    .map((goal) => (goal?.[field] ? dayjs(goal[field]).valueOf() : Number.NaN))
    .filter(Number.isFinite);
  if (!dates.length) return null;
  return sort === 'newest' ? Math.max(...dates) : Math.min(...dates);
};

export const sortTeamGoalMembers = (
  members: TeamGoalMember[],
  sort: MyGoalsSort,
): TeamGoalMember[] =>
  [...members].sort((first, second) => {
    if (!first.goals.length) return second.goals.length ? 1 : 0;
    if (!second.goals.length) return -1;

    const firstValue = memberSortValue(first, sort);
    const secondValue = memberSortValue(second, sort);
    if (firstValue === null) return secondValue === null ? 0 : 1;
    if (secondValue === null) return -1;

    return sort === 'highest_progress' || sort === 'newest'
      ? secondValue - firstValue
      : firstValue - secondValue;
  });

export const groupTeamGoalsByStatus = (
  members: TeamGoalMember[],
  sort?: MyGoalsSort,
): TeamGoalStatusSection[] => {
  const uniqueGoals = Array.from(
    new Map(
      members
        .flatMap((member) => member.goals || [])
        .filter((goal) => goal?.id)
        .map((goal) => [goal.id, goal]),
    ).values(),
  );

  return teamStatusOrder
    .map((performance) => {
      const goals = uniqueGoals.filter(
        (goal) => goalAttentionStatus(goal) === performance,
      );
      return {performance, goals: sort ? sortMyGoals(goals, sort) : goals};
    })
    .filter((section) => section.goals.length > 0);
};

export const teamPerformanceItems = (summary: TeamGoalSummary) => {
  const order: GoalAttentionStatus[] = [
    'overdue',
    ...goalHealthBarOrder,
    'upcoming',
    'no_status',
  ];
  return order
    .filter((performance) => summary.byPerformance[performance] > 0)
    .map((performance) => ({
      performance,
      count: summary.byPerformance[performance],
      label: teamStatusLabels[performance],
    }));
};

export const teamHealthBarItems = (summary: TeamGoalSummary) => {
  const total = goalHealthBarOrder.reduce(
    (count, status) => count + summary.byPerformance[status],
    0,
  );

  return goalHealthBarOrder
    .filter((status) => summary.byPerformance[status] > 0)
    .map((performance) => ({
      performance,
      count: summary.byPerformance[performance],
      width: total ? (summary.byPerformance[performance] / total) * 100 : 0,
    }));
};

export const teamPerformanceSummary = (summary: TeamGoalSummary) =>
  teamPerformanceItems(summary)
    .map(({count, label}) => `${count} ${label}`)
    .join(' · ');
